import React, {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FiltersProvider } from '@Sage/ssm-web5';
import { CONTACTS } from 'constants/Entities';
import { EntityFiltersActions, EntityListSelectActions, ServerListActions } from 'actions';
import MarketingStatusSelect, { MARKETING_STATUS } from 'components/TableCells/MarketingStatusCell';
import { getLiteral } from 'utils/getLiteral';
import FiltersLayout from 'containers/components/layouts/FiltersLayout';
import EntityMenu from 'containers/components/EntityMenu';
import NewEntityList from 'containers/components/NewEntityList';
import SearchContacts from 'containers/contacts/components/SearchContacts';
import CONFIG from './RecipientsContactsTableConfig';
import {
    getMarketingStatusListFromState,
    getSubscribedMarketingStatusOption,
    hasSubscribedFilter,
    INITIAL_SUBSCRIBED_FILTER_VALUE,
    MARKETING_STATUS_LIST,
} from './marketingStatusFilterUtils';

const FILTER_KEY = 'recipientsContacts';

function mapStateToProps(state) {
    const selection = state.entityListSelect[CONTACTS.entity];
    const selected = selection?.selected || {};
    const includedIds = Object.keys(selected).filter((id) => selected[id] === true);

    return {
        allSelected: selection?.allSelected,
        bulkSelectedEntities: selection?.total ?? 0,
        filters: state.entityFilters?.[FILTER_KEY]?.filters,
        marketingStatusList: getMarketingStatusListFromState(state),
        selectedIds: includedIds,
        shouldClearWeb5Checkboxes: selection?.shouldClearWeb5Checkboxes,
        marketingCampaignsEnabled: state?.config?.userData?.marketingCampaigns,
        canEdit: !!state?.config?.permission?.crud_permission?.[CONTACTS.permission]?.update,
    };
}

const mapDispatchToProps = (dispatch) => ({
    onEntitySelect: bindActionCreators(EntityListSelectActions, dispatch).onEntitySelect,
    onEntitySelectAll: bindActionCreators(EntityListSelectActions, dispatch).onEntitySelectAll,
    changeFilter: bindActionCreators(EntityFiltersActions, dispatch).changeFilter,
    hideFilters: bindActionCreators(EntityFiltersActions, dispatch).hideFilters,
    getMarketingStatusList: bindActionCreators(ServerListActions, dispatch).getList,
    setShouldClearWeb5Checkboxes: bindActionCreators(EntityListSelectActions, dispatch)
        .setShouldClearWeb5Checkboxes,
    syncEntityListSelectionToken: bindActionCreators(EntityListSelectActions, dispatch)
        .syncEntityListSelectionToken,
});

const RecipientsContactsTab = memo(
    ({
        allSelected,
        bulkSelectedEntities,
        changeFilter,
        filters,
        getMarketingStatusList,
        marketingStatusList,
        selectionToken,
        shouldApplyInitialMarketingStatus,
        onInitialMarketingStatusApplied,
        onNonSubscribedSelectionChange,
        shouldClearWeb5Checkboxes,
        marketingCampaignsEnabled,
        canEdit,
        onEntitySelect,
        onEntitySelectAll,
        setShouldClearWeb5Checkboxes,
        hideFilters,
        syncEntityListSelectionToken,
    }) => {
        const tableRef = useRef(null);

        useLayoutEffect(() => {
            hideFilters(CONTACTS, FILTER_KEY);
        }, [hideFilters]);

        useLayoutEffect(() => {
            if (!selectionToken) {
                return;
            }

            syncEntityListSelectionToken({
                entity: CONTACTS,
                data: { token: selectionToken, entities: bulkSelectedEntities },
                selectionFor: 'marketingCampaigns',
                selectionEntity: 'contacts',
            });
        }, [selectionToken, syncEntityListSelectionToken]);

        const areAllChecked = useRef(false);
        const [nonSubscribedCount, setNonSubscribedCount] = useState(0);

        const subscribedMarketingStatusOption = useMemo(
            () => getSubscribedMarketingStatusOption(marketingStatusList),
            [marketingStatusList],
        );

        useEffect(() => {
            if (!shouldApplyInitialMarketingStatus || subscribedMarketingStatusOption) {
                return;
            }

            getMarketingStatusList(MARKETING_STATUS_LIST).catch(() => null);
        }, [
            getMarketingStatusList,
            shouldApplyInitialMarketingStatus,
            subscribedMarketingStatusOption,
        ]);

        useLayoutEffect(() => {
            const updates = [];
            let didApplyInitialMarketingStatus = false;

            if (filters?.entityShowSelected?.value !== 'marketingCampaigns') {
                updates.push({
                    filter: {
                        id: 'entityShowSelected',
                        hideForCount: true,
                        invisible: true,
                        noCacheCheck: true,
                    },
                    value: 'marketingCampaigns',
                });
            }

            if (
                shouldApplyInitialMarketingStatus &&
                subscribedMarketingStatusOption &&
                !hasSubscribedFilter(filters?.marketingStatus?.value)
            ) {
                updates.push({
                    filter: {
                        id: 'marketingStatus',
                        dataType: 'singleValueList',
                        description: getLiteral('label_marketing_status'),
                        hideForCount: true,
                        inputAttrs: {
                            list: 'tblMarketingStatus',
                            isMulti: true,
                        },
                    },
                    value: INITIAL_SUBSCRIBED_FILTER_VALUE,
                    completeValues: [subscribedMarketingStatusOption],
                });
                didApplyInitialMarketingStatus = true;
            }

            if (!updates.length) {
                return;
            }

            updates.forEach((update, index) => {
                const isLast = index === updates.length - 1;
                changeFilter({
                    entity: CONTACTS,
                    filter: update.filter,
                    value: update.value,
                    completeValues: update.completeValues,
                    filterKey: FILTER_KEY,
                    refresh: isLast,
                });
            });

            if (didApplyInitialMarketingStatus) {
                onInitialMarketingStatusApplied?.('contacts');
            }
        }, [
            changeFilter,
            filters?.entityShowSelected?.value,
            filters?.marketingStatus?.value,
            subscribedMarketingStatusOption,
            onInitialMarketingStatusApplied,
            shouldApplyInitialMarketingStatus,
        ]);

        useEffect(() => {
            onNonSubscribedSelectionChange?.(nonSubscribedCount);
        }, [nonSubscribedCount, onNonSubscribedSelectionChange]);

        useEffect(() => {
            if (shouldClearWeb5Checkboxes) {
                if (tableRef?.current?.clearCheckboxes) tableRef.current.clearCheckboxes();
                setShouldClearWeb5Checkboxes(CONTACTS, false);
                setNonSubscribedCount(0);
            }
        }, [setShouldClearWeb5Checkboxes, shouldClearWeb5Checkboxes]);

        useEffect(() => {
            areAllChecked.current = allSelected;
        }, [allSelected]);

        const handleOnRef = useCallback((ref) => {
            tableRef.current = ref;
        }, []);

        const onClickCheckbox = useCallback(
            ({ id, isColumn, value, checked, data, rowIndex, total }) => {
                if (isColumn) {
                    onEntitySelectAll(CONTACTS, value, checked, total, FILTER_KEY);
                    setNonSubscribedCount(0);
                } else {
                    onEntitySelect(CONTACTS, id, value, data, rowIndex, checked);
                    const statusId = Number(
                        data?.marketingStatusId ?? data?.MarketingStatusId ?? 0,
                    );
                    if (statusId !== MARKETING_STATUS.SUBSCRIBED) {
                        setNonSubscribedCount((prev) =>
                            checked ? prev + 1 : Math.max(0, prev - 1),
                        );
                    }
                }
            },
            [onEntitySelect, onEntitySelectAll],
        );

        const config = useMemo(
            () =>
                CONFIG({
                    onClickCheckbox,
                    selectionToken,
                    marketingCampaignsEnabled,
                    canEdit,
                }),
            [onClickCheckbox, selectionToken, marketingCampaignsEnabled, canEdit],
        );

        const getCheckedRows = useCallback(
            (data) => {
                const columnName = config?.columnDefs?.[0]?.colId;
                if (!columnName) {
                    return {};
                }

                const checkedRows = data.reduce((acc, current) => {
                    if (!current) {
                        return acc;
                    }

                    if (!acc[columnName]) {
                        acc[columnName] = {
                            rows: {},
                            areAllChecked: areAllChecked.current,
                            lazyAreAllChecked: areAllChecked.current,
                        };
                    }

                    acc[columnName].rows[current.Id] = current.selected === '1';
                    return acc;
                }, {});

                if (checkedRows[columnName]) {
                    checkedRows[columnName].areAllChecked = areAllChecked.current;
                    checkedRows[columnName].lazyAreAllChecked = areAllChecked.current;
                }

                return checkedRows;
            },
            [config],
        );

        if (!config) return null;

        return (
            <FiltersProvider>
                <div className="fm-select-recipients-tab">
                    <EntityMenu
                        entity={CONTACTS}
                        filterKey={config.filterKey}
                        withFilters={true}
                        quickFilters={config.quickFilters}
                        readOnlyFilters={config.readOnlyFilters}
                        readOnlyQuickFilters={config.readOnlyQuickFilters}
                        dontIgnoreTheseFilters={config.dontIgnoreTheseFilters}
                        useQuickView={config.useQuickView}
                        searchComponent={
                            <SearchContacts filterKey={config.filterKey} useAsSimpleSearch />
                        }
                        withBulkOperations={false}
                        hideDefaultActions={true}
                        canMassiveUpdate={false}
                        canMassiveDelete={false}
                        canMerge={false}
                    />
                    <FiltersLayout
                        config={config}
                        className="fm-select-recipients-tab__list-layout fm-filters-layout--list-side-gap"
                    >
                        <NewEntityList
                            id="fm-grid-recipients-contacts"
                            entity={CONTACTS}
                            config={config}
                            filterKey={config.filterKey}
                            initOnReady={true}
                            useLazyLoad={true}
                            useCache={false}
                            useSort={true}
                            useSelectColumns={true}
                            useFieldSelector={false}
                            discardCount={true}
                            domLayout="normal"
                            components={{ MarketingStatusSelect }}
                            handleOnRef={handleOnRef}
                            getCheckedRows={getCheckedRows}
                            preventInitEntitySelect={!!selectionToken}
                        />
                    </FiltersLayout>
                </div>
            </FiltersProvider>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(RecipientsContactsTab);
